import React from "react";
import { useMessages, useConfirm } from "@tiny/components";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { closeSnackbar } from "notistack";
import { usePutSegmentCleanupFileDownloadQuery } from "@tiny/api";

export interface CleanedListHistoryRowProps {
  item: {
    id: string;
    fileName: string;
    cleanupAt: string;
  };
  onDownloadComplete?: () => void;
}

export function useCleanedListHistoryRow(props: CleanedListHistoryRowProps) {
  const { item, onDownloadComplete } = props;
  const { showErrorMessage } = useMessages();
  const showConfirm = useConfirm();

  const [downloadId, setDownloadId] = React.useState<string | null>(null);

  const { refetch: downloadFile } = usePutSegmentCleanupFileDownloadQuery(
    { cleanedHistoryFileId: downloadId || "" },
    {
      enabled: false,
      onSuccess: async (data) => {
        try {
          const response = await fetch(data.url);
          const blob = await response.blob();
          const contentDisposition =
            response.headers.get("content-disposition") ||
            `fileName=${item.fileName}`;
          const fileName =
            contentDisposition.split("fileName=")[1] || item.fileName;

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          closeSnackbar();
          showUploadMessage({
            variant: "success",
            message: "Downloaded successfully!",
            description: "Your cleaned list has been downloaded successfully!",
            primaryCtaActionText: "Close",
            primaryCtaActionFunction: () => {
              closeSnackbar();
            },
            persist: false,
          });

          onDownloadComplete?.();
        } catch (e: any) {
          closeSnackbar();
          showErrorMessage("Failed to download file");
        } finally {
          setDownloadId(null);
        }
      },
      onError: (e: any) => {
        closeSnackbar();
        extractBackendError(e.response).then((be) => {
          showErrorMessage(getErrorMessage(be));
        });
        setDownloadId(null);
      },
    }
  );

  React.useEffect(() => {
    if (downloadId) {
      showUploadMessage({
        variant: "uploading",
        message: "Downloading",
        description: "Your cleaned list is downloading",
        persist: true,
        showClose: true,
      });
      downloadFile();
    }
  }, [downloadId, downloadFile]);

  const downloadCleanedFile = React.useCallback(
    async (cleanedHistoryFileId: string) => {
      showConfirm({
        title: `Do you want to download ${item.fileName}?`,
        message: "The cleaned list will be downloaded to your device.",
        actions: [
          {
            label: "No",
            onClick: () => {
              return;
            },
          },
          {
            label: "Yes",
            primary: true,
            onClick: () => {
              setDownloadId(cleanedHistoryFileId);
            },
          },
        ],
      });
    },
    [showConfirm, item.fileName]
  );

  return {
    downloadCleanedFile,
  };
}
